import { filter, startCase, sortBy, toLower } from 'lodash';
import { produce } from 'immer';
import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import AgentStatusReasonsEditor from './AgentStatusReasonEditor';
import agentStatusReasonsResource from 'src/actions/agentStatus/agentStatusReasonsResource';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  OverflowMenuText,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from '../shared/TablePage';
import AdminTooltip, { Message } from 'src/presentation/components/common/AdminTooltip';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';

const columnConfig = [
  { attr: 'name', label: 'Name', customRenderer: item => <Name reason={item} /> },
  { attr: 'type', label: 'Type', customRenderer: item => startCase(toLower(item.type)) },
];
const tabConfig = [
  { label: 'Status', filter: reason => !reason.disabled, key: `agentStatus-page-tab-enabled` },
  { label: 'Archived', filter: reason => reason.disabled, key: `agentStatus-page-tab-disabled` },
];

const AgentStatusReasons: TablePageRender = ({ root }) => {
  const actions = (
    <RouterLinkButton data-aid="agentStatus-addNew-button" to="agent-status/new">
      Create Agent Status
    </RouterLinkButton>
  );
  const { errors, loadingState, values } = useResource(agentStatusReasonsResource);
  const agentStatusReasons = sortBy(values, item => [item.type, item.name.toLowerCase()]);
  const description = (
    <span>
      Agents choose one of these statuses when they are working. You can create an Active or Away Status. When a status
      is set as default, agents will be set to this status upon logging in or coming back from idle and away. To learn
      more about Agent Status, visit the{' '}
      <ExternalLink label="Help Docs" to="https://help.gladly.com/docs/what-are-away-and-active-agent-status" />. To see
      reporting details, visit the{' '}
      <ExternalLink label="Agent Status Report" to="https://help.gladly.com/docs/reports-used-for-agent-status" />.
    </span>
  );

  const activeCount = filter(agentStatusReasons, item => {
    return item.type === 'ACTIVE' && !item.disabled;
  }).length;
  const awayCount = filter(agentStatusReasons, item => {
    return item.type === 'AWAY' && !item.disabled;
  }).length;

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    return filter(items, item => {
      return item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
    });
  }

  const renderRowActions = ({ item }) => (
    <Actions activeCount={activeCount} awayCount={awayCount} item={item} root={root} />
  );

  return (
    <TablePage
      actions={actions}
      columnConfig={columnConfig}
      data-aid="agentStatus-page"
      description={description}
      editor={AgentStatusReasonsEditor}
      errors={errors}
      filterFunc={filterFunc}
      isLoading={isLoadingStatus(loadingState)}
      items={agentStatusReasons}
      name="Agent Status"
      renderRowActions={renderRowActions}
      resetErrors={agentStatusReasonsResource.actions.resetErrors}
      root={root}
      tabConfig={tabConfig}
    />
  );
};

function Name({ reason }) {
  return (
    <NameColumn>
      <StyledName title={reason.name}>{reason.name}</StyledName>
      {reason.default ? <DefaultPill>Default</DefaultPill> : null}
    </NameColumn>
  );
}

const DefaultPill = styled.span`
  background-color: ${p => p.theme.colors.purple400}};
  border-radius: 12px;
  color: ${p => p.theme.colors.white};
  font-size: ${p => p.theme.fontSize.small};
  margin-left: 8px;
  padding: 2px 8px;
`;
const StyledName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: no-wrap;
`;
const NameColumn = styled.div`
  display: flex;
  flex-direction: row;
`;

function Actions({ activeCount, awayCount, item, root }) {
  const { onClose, ...overFlowProps } = usePopoverMenu();
  const dispatch = useDispatch();

  const toggleArchive = () => {
    const toast = `We ${item.disabled ? 'un' : ''}archived ${item.name}`;
    const updatedItem = produce(item, draft => {
      draft.disabled = !draft.disabled;
    });
    dispatch(
      agentStatusReasonsResource.effects.update({ id: item.id }, updatedItem, {
        customToastMsg: toast,
        disableWindowBack: true,
      })
    );
  };

  const setAsDefault = () => {
    const toast = `Successfully set ${item.name} as the new default status`;
    const updatedItem = produce(item, draft => {
      draft.default = true;
    });
    dispatch(
      agentStatusReasonsResource.effects.update({ id: item.id }, updatedItem, {
        customToastMsg: toast,
        disableWindowBack: true,
      })
    );
  };

  const archiveText = item.disabled ? 'Unarchive' : 'Archive';
  let archiveContent;
  if (
    (item.type === 'ACTIVE' && activeCount === 1 && !item.disabled) ||
    (item.type === 'AWAY' && awayCount === 1 && !item.disabled)
  ) {
    archiveContent = (
      <AdminTooltip
        message={
          <Message>
            At least one {item.type.toLowerCase()} status is required.
            <br />
            Create a new {item.type.toLowerCase()} status before archiving this status.
          </Message>
        }
        position="bottom"
      >
        {archiveText}
      </AdminTooltip>
    );
  } else if (item.type === 'ACTIVE' && item.default) {
    archiveContent = (
      <AdminTooltip
        message={
          <Message>
            The default status cannot be archived.
            <br />
            Set a new default status before archiving this status.
          </Message>
        }
        position="bottom"
      >
        {archiveText}
      </AdminTooltip>
    );
  } else {
    archiveContent = archiveText;
  }

  return (
    <OverflowMenu id={item.id} onClose={onClose} {...overFlowProps}>
      <OverflowMenuItem data-aid="agentStatus-edit">
        <OverflowMenuLink data-aid="agentStatus-edit" to={`${root}/${item.id}`}>
          Edit
        </OverflowMenuLink>
      </OverflowMenuItem>
      {item.type === 'ACTIVE' && !item.disabled ? (
        <OverflowMenuItem
          data-aid="agentStatus-setAsDefault"
          isDisabled={item.type === 'ACTIVE' && item.default}
          onClick={setAsDefault}
        >
          <OverflowMenuText data-aid="agentStatus-setAsDefault">Set as default</OverflowMenuText>
        </OverflowMenuItem>
      ) : null}
      <OverflowMenuItem
        data-aid="agentStatus-archive"
        isDisabled={
          (item.type === 'ACTIVE' && item.default) || (item.type === 'AWAY' && awayCount === 1 && !item.disabled)
        }
        onClick={toggleArchive}
      >
        <OverflowMenuText data-aid="agentStatus-archive">{archiveContent}</OverflowMenuText>
      </OverflowMenuItem>
    </OverflowMenu>
  );
}

export default AgentStatusReasons;
